 
 
import * as L from 'leaflet';

import { Subject } from 'rxjs';

import { HttpClient } from '@angular/common/http';
import { Legend } from '../controls/legendControl/legend';
import { LayerOptions } from './options';
import { BgsLeafletComponent } from '../bgsLeaflet.component';
import { FeatureDisplayItemGenerator } from '../featureDisplay/featureDisplayItemGenerator';
import { FeatureDisplayItem } from '../featureDisplay/featureDisplayItem';
import { SimpleConfigValue } from '../../../objects/configAttributes/simpleConfigValue';
import { SimpleConfigObservableWithValue } from '../../../objects/configAttributes/simpleConfigObservableWithValue';

export abstract class MapLayer {
  public id: string;
  public name: string;

  public visibleOnLayerControl = new SimpleConfigValue<this, boolean>(this, true);
  public tryToRender = new SimpleConfigValue<this, boolean>(this, true);
  public addedToMap = new SimpleConfigObservableWithValue<this, null | boolean>(this);
  public toggleable = new SimpleConfigObservableWithValue<this, boolean>(this, true);
  public hidden = new SimpleConfigObservableWithValue<this, boolean>(this, false);

  public bringToFront = new SimpleConfigObservableWithValue<this, number>(this, 0);
  public bringToBack = new SimpleConfigObservableWithValue<this, number>(this, 0);

  public options = new LayerOptions<this>(this);

  protected clickSource = new Subject<L.LeafletMouseEvent>();
  protected layerClickFeatureItemGenerator: null | FeatureDisplayItemGenerator;

  protected legendCreatorFunction: (layer: MapLayer, http: HttpClient) => Promise<null | Array<Legend>>;

  protected bgsLeaflet: BgsLeafletComponent;
  protected leafletWrapperLayer: L.LayerGroup;
  protected leafletMapLayer: L.Layer;

  protected constructor(id: string, name?: string) {
    this.id = id;
    this.leafletWrapperLayer = new L.LayerGroup();
    this.leafletWrapperLayer['id'] = id;

    this.name = name || id;

    this.options.opacity.watch().subscribe(() => {
      this.updateLeafletLayerOpacity();
    });
    this.hidden.watch().subscribe(() => {
      this.updateHidden();
    });

    this.bringToFront.watch().subscribe((refIndex: number) => this.bringLayerToFront(refIndex));
    this.bringToBack.watch().subscribe((refIndex: number) => this.bringLayerToBack(refIndex));
  }

  public addSelfToMap(bgsLeaflet: BgsLeafletComponent): Promise<void> {
    // console.debug('addSelfToMap', this.id);
    return new Promise<void>((resolve) => {
      if (this.bgsLeaflet == null) {
        this.bgsLeaflet = bgsLeaflet;
      }
      const leafletMap = bgsLeaflet.getLeafletObject();
      if (!this.tryToRender.get()) {
        this.addedToMap.set(false);
        resolve();
      } else {
        this.addedToMap.set(null);

        resolve(
          this.userPreLayerAddFunction()
            .then(() => this.preLayerAdd())
            .then(() => this.getLeafletLayer())
             
            .then((leafletLayer: L.Layer) => {
              // console.debug('Adding Layer', this.id);
              return new Promise<void>((resolve2) => {
                if (leafletLayer != null) {
                  this.leafletMapLayer = leafletLayer;
                  this.leafletWrapperLayer.addTo(leafletMap);

                  this.checkOpacityOnAdd();

                  void this.ensureAddedToMap().then(() => {
                    this.addedToMap.set(true);
                    resolve2();
                  });
                } else {
                  // TODO: SHOW NOTIFICATION!
                  console.warn('Generated Map Layer was NULL!');
                  this.addedToMap.set(false);
                  resolve2();
                }
              });
            })
            .then(() => this.postLayerAdd())
            .then(() => this.updateHidden())
            .then(() => this.userPostLayerAddFunction(this.leafletMapLayer)),
          // TEST - FAKE DELAY
          // .then(() => new Promise<void>((resolve3) => {
          //   setTimeout(() => {
          //     resolve3();
          //   }, 10000);
          // }))
        );
      }
    });
  }

  public removeSelfFromMap(): Promise<void> {
    const leafletWrapperLayer = this.getLeafletLayerFromMap();
    return leafletWrapperLayer == null
      ? Promise.resolve()
      : this.userPreLayerRemoveFunction(this.leafletMapLayer)
        .then(() => this.preLayerRemove())
         
        .then(() => {
          return new Promise<void>((resolve) => {
            // remove the layer from the wrapper so that it can be refreshed if added again
            if (this.leafletWrapperLayer.hasLayer(this.leafletMapLayer)) {
              this.leafletWrapperLayer.removeLayer(this.leafletMapLayer);
            }
            // remove wrapper layer from map
            this.bgsLeaflet.getLeafletObject().removeLayer(this.leafletWrapperLayer);
            const iterationLimit = 20;
            let county = 0;
            const removeIntervalId = setInterval(() => {
              county++;
              if (null == this.getLeafletLayerFromMap() || county > iterationLimit) {
                if (county > iterationLimit) {
                  console.warn('Failed to verify layer removed', this.id);
                }
                clearInterval(removeIntervalId);
                resolve();
              }
            }, 100);
          });
        })
        .then(() => this.postLayerRemove())
        .then(() => this.userPostLayerRemoveFunction(this.leafletMapLayer));
  }

  public setPreLayerAddFunction(func: null | (() => Promise<void>)): this {
    this.userPreLayerAddFunction = (null != func) ? func : () => Promise.resolve();
    return this;
  }
  public setPostLayerAddFunction(func: null | ((leafletLayer: L.Layer) => Promise<void>)): this {
    this.userPostLayerAddFunction = (null != func) ? func : () => Promise.resolve();
    return this;
  }
  public setPreLayerRemoveFunction(func: null | ((leafletLayer: L.Layer) => Promise<void>)): this {
    this.userPreLayerRemoveFunction = (null != func) ? func : () => Promise.resolve();
    return this;
  }
  public setPostLayerRemoveFunction(func: null | ((leafletLayer: L.Layer) => Promise<void>)): this {
    this.userPostLayerRemoveFunction = (null != func) ? func : () => Promise.resolve();
    return this;
  }

  public setLegendCreatorFunction(
    legendCreatorFunction: (layer: MapLayer, http: HttpClient) => Promise<null | Array<Legend>>,
  ): this {
    this.legendCreatorFunction = legendCreatorFunction;
    return this;
  }
  public getLegendData(http: HttpClient): Promise<null | Array<Legend>> {
    // returns a promise or null.
    // Catches any problems with the Promise and returns null for the legendArray value
    return Promise.resolve<null | Array<Legend>>(
      this.legendCreatorFunction
        ? this.legendCreatorFunction(this, http).catch(() => {
          console.warn('Legend Data Function Failed.', this.id);
          return null;
        })
        : Promise.resolve(null),
    );
  }

  public click(clickEvent: L.LeafletMouseEvent): void {
    this.clickSource.next(clickEvent);
  }

  public addClickListener(func: (clickEvent: L.LeafletMouseEvent) => void): this {
    this.clickSource.subscribe(func);
    return this;
  }

  public setLayerClickFeatureItemGenerator(featureItemGenerator: null | FeatureDisplayItemGenerator): this {
    this.layerClickFeatureItemGenerator = featureItemGenerator;
    return this;
  }

  public getLayerClickFeatureItemGenerator(): null | FeatureDisplayItemGenerator {
    return this.layerClickFeatureItemGenerator;
  }

  public getLayerClickFeatureItem(
    clickEvent: L.LeafletMouseEvent,
    http: HttpClient,
  ): Promise<Array<FeatureDisplayItem>> {
    return this.layerClickFeatureItemGenerator
      ? this.layerClickFeatureItemGenerator.getFeatureDisplayItems(clickEvent, http)
      : Promise.resolve(new Array<FeatureDisplayItem>());
  }

  public getBgsLeaflet(): BgsLeafletComponent {
    return this.bgsLeaflet;
  }

  protected userPreLayerAddFunction: () => Promise<void> = () => Promise.resolve();
  protected userPostLayerAddFunction: (leafletLayer: L.Layer) => Promise<void> = () => Promise.resolve();
  protected userPreLayerRemoveFunction: (leafletLayer: L.Layer) => Promise<void> = () => Promise.resolve();
  protected userPostLayerRemoveFunction: (leafletLayer: L.Layer) => Promise<void> = () => Promise.resolve();

  // override thes for layer specific operations
  protected preLayerAdd(): Promise<void> {
    return Promise.resolve();
  }
  protected postLayerAdd(): Promise<void> {
    return Promise.resolve();
  }
  protected preLayerRemove(): Promise<void> {
    return Promise.resolve();
  }
  protected postLayerRemove(): Promise<void> {
    return Promise.resolve();
  }

  protected ensureAddedToMap(): Promise<void> {
    // called every x secs to see if it's been fully added yet
    return new Promise<void>((resolve) => {
      const iterationLimit = 20;
      let county = 0;
      const refreshIntervalId = setInterval(() => {
        county++;
        if (null != this.getLeafletLayerFromMap() || county > iterationLimit) {
          if (county > iterationLimit) {
            console.warn('Failed to verify layer add', this.id);
          }
          clearInterval(refreshIntervalId);
          resolve();
        }
      }, 100);
    });
  }
  protected getLeafletLayerFromMap(): null | L.Layer {
    let returnVal: null | L.Layer = null;
    if (this.bgsLeaflet != null) {
      this.bgsLeaflet.getLeafletObject().eachLayer((leafletLayer: L.Layer) => {
        if (this.id === leafletLayer['id']) {
          returnVal = leafletLayer;
        }
      });
    }
    return returnVal;
  }

  protected checkOpacityOnAdd(): void {
    if (this.options.opacity.get() == null) {
      this.options.opacity.set(0.9);
    } else {
      // some layers that don't support "setOpacity" might need a kick (e.g. HeatLayer)
      this.options.opacity.trigger();
    }
  }

  protected updateHidden(): void {
    if (this.leafletMapLayer != null) {
      if (this.hidden.get() && this.leafletWrapperLayer.hasLayer(this.leafletMapLayer)) {
        this.leafletWrapperLayer.removeLayer(this.leafletMapLayer);
      } else if (!this.hidden.get() && !this.leafletWrapperLayer.hasLayer(this.leafletMapLayer)) {
        this.leafletWrapperLayer.addLayer(this.leafletMapLayer);
      }
    }
  }

  protected updateLeafletLayerOpacity(): void {
    // console.debug('updateLeafletLayerOpacity');
    // set the layer is on map and it has a "setOpacity" function, use it
    // console.debug('this.leafletMapLayer', this.leafletMapLayer);
    if (this.leafletMapLayer != null && typeof this.leafletMapLayer['setOpacity'] === 'function') {
      this.leafletMapLayer['setOpacity'](this.options.opacity.get());
    }
  }

  protected ensurePaneExists(paneId: null | string, zIndex: number): void {
    if ((null != paneId) && (this.bgsLeaflet != null)) {
      const pane = this.bgsLeaflet.getLeafletObject().getPane(paneId);
      if (pane == null) {
        const newPane = this.bgsLeaflet.getLeafletObject().createPane(paneId);
        newPane.style.zIndex = String(zIndex);
      }
    }
  }

  // if this errors, then you need to override it
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected bringLayerToFront(refIndex: number): void {
    if (this.leafletMapLayer != null && this.leafletWrapperLayer.hasLayer(this.leafletMapLayer)) {
      this.leafletMapLayer['bringToFront']();
    }
  }
  // if this errors, then you need to override it
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected bringLayerToBack(refIndex: number): void {
    if (this.leafletMapLayer != null && this.leafletWrapperLayer.hasLayer(this.leafletMapLayer)) {
      this.leafletMapLayer['bringToBack']();
    }
  }

  protected abstract getLeafletLayer(): Promise<null | L.Layer>;
}
